/**
 * Shared utilities for {@link EstablishmentRow}, {@link EstablishmentCard} and {@link EstablishmentsDataTable}.
 */
import type { SelectOption } from '@/components/forms/Select';
import type { Establishment, EstablishmentListParams } from '@/types/establishment.types';
import { toLocalDateString } from '@/utils/format-date-utils';
import type { EstablishmentFilters } from './establishment-configs';

/** Students / tutors / VSC tracking labels for a row. */
export interface EstablishmentTrackingDisplay {
  students: string;
  tutors: string;
  vsc: string;
}

/** `actual / target` tracking cell; missing target is an em dash. */
export function formatTrackingValue(
  real: number | null | undefined,
  target: number | null | undefined,
): string {
  const realDisplay = real ?? 0;
  const targetDisplay = target === null || target === undefined ? '—' : String(target);
  return `${realDisplay} / ${targetDisplay}`;
}

/** Parse optional quota form values; preserves 0, treats empty as undefined. */
export function parseOptionalQuotaValue(
  value: string | number | null | undefined,
): number | undefined {
  if (value === '' || value === null || value === undefined) {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
}

/** Maps Engagement KPI fields to tracking column display (actual / target). */
export function getEstablishmentTrackingDisplay(
  establishment: Pick<
    Establishment,
    | 'studentCount'
    | 'studentQuota'
    | 'tutorCount'
    | 'volunteersToRecruit'
    | 'vscCount'
    | 'volunteerQuota'
  >,
): EstablishmentTrackingDisplay {
  return {
    students: formatTrackingValue(establishment.studentCount, establishment.studentQuota),
    tutors: formatTrackingValue(establishment.tutorCount, establishment.volunteersToRecruit),
    vsc: formatTrackingValue(establishment.vscCount, establishment.volunteerQuota),
  };
}

/**
 * Computes a human-readable seniority string from a partnership start date.
 * Returns null if start date is missing or in the future.
 */
export function computePartnershipSeniority(
  startDate: Date | string | null | undefined,
): string | null {
  if (!startDate) return null;
  const start = new Date(startDate);
  const now = new Date();
  const totalMonths =
    (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (totalMonths < 0) return null;
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  if (years === 0) return `${months} mois`;
  if (months === 0) return `${years} an${years > 1 ? 's' : ''}`;
  return `${years} an${years > 1 ? 's' : ''} ${months} mois`;
}

/** Returns the badge color name for an establishment status. */
export function getEstablishmentStatusColor(status: string): 'success' | 'error' {
  return status === 'active' ? 'success' : 'error';
}

/** `"Name (code)"` — skips a second code if the name already has it. */
export function formatEstablishmentDepartment(
  department?: string | null,
  departmentCode?: string | null,
): string {
  const name = department?.trim() || '';
  const code = departmentCode?.trim() || '';
  if (name && code) {
    if (name.includes(`(${code})`)) return name;
    return `${name} (${code})`;
  }
  return name || code;
}

/** Coordinator names (email fallback), comma-joined, or an em dash. */
export function formatEstablishmentCoordinatorsLabel(
  coordinators:
    | Array<{ firstName?: string | null; lastName?: string | null; email?: string | null }>
    | undefined,
): string {
  if (!coordinators?.length) return '—';
  return coordinators
    .map((coordinator) => {
      const full = `${coordinator.firstName || ''} ${coordinator.lastName || ''}`.trim();
      return full || coordinator.email || '—';
    })
    .join(', ');
}

/** Opening delay in days when prospection is `valide`; otherwise an em dash. */
export function formatEstablishmentOpeningDelay(
  prospectionStatus: string | undefined,
  openingDelayDays: number | null | undefined,
): string {
  if ((prospectionStatus ?? 'to_define') === 'valide' && openingDelayDays != null) {
    return `${openingDelayDays} j`;
  }
  return '—';
}

/** `city · department` line; skips empty parts. */
export function formatEstablishmentLocationLine(
  city: string | null | undefined,
  department: string | null | undefined,
  departmentCode: string | null | undefined,
): string {
  return [city?.trim(), formatEstablishmentDepartment(department, departmentCode)]
    .filter(Boolean)
    .join(' · ');
}

/** Formats IPS as `value` or `value (year)` for details. */
export function formatIpsValue(
  ipsValue: number | string,
  ipsYear?: number | string | null,
): string {
  if (!ipsYear) return String(ipsValue);
  return `${ipsValue} (${ipsYear})`;
}

/** Formats QPV as a yes / no / empty label for table and details. */
export function formatQpvLabel(isQpv: boolean | null | undefined): string {
  if (isQpv === true) return 'Oui';
  if (isQpv === false) return 'Non';
  return '—';
}

/** Local `YYYY-MM-DD` for {@link DatePicker} filter values. */
export function toEstablishmentFilterDateString(d?: Date): string | undefined {
  if (!d) return undefined;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Drop a trailing ` (…)` suffix from an autocomplete label. */
export function stripEstablishmentTrailingParenSuffix(label: string): string {
  const open = label.lastIndexOf(' (');
  if (open === -1 || !label.endsWith(')')) return label.trim();
  return label.slice(0, open).trim();
}

/** Parse a filter start date at local midnight. */
export function parseEstablishmentFilterStartDate(val: string): Date {
  const [year, month, day] = val.split('-').map(Number);
  return new Date(year, month - 1, day, 0, 0, 0);
}

/** Parse a filter end date at local end-of-day. */
export function parseEstablishmentFilterEndDate(val: string): Date {
  const [year, month, day] = val.split('-').map(Number);
  return new Date(year, month - 1, day, 23, 59, 59, 999);
}

/** Prepend the selected establishment when it is missing from the page. */
export function mergeEstablishmentAutocompleteOptions(
  options: SelectOption[],
  selectedId: string | undefined,
  selectedEstablishment: { id: string; name: string } | null | undefined,
): SelectOption[] {
  if (!selectedId || !selectedEstablishment) return options;
  const exists = options.some((o) => o.value === selectedId);
  if (exists) return options;
  return [{ value: selectedEstablishment.id, label: selectedEstablishment.name }, ...options];
}

/** Map UI filters to list-API query params (empty arrays become `undefined`). */
export function toEstablishmentListParams(filters: EstablishmentFilters): EstablishmentListParams {
  return {
    types: filters.types.length > 0 ? filters.types : undefined,
    categories:
      filters.categories && filters.categories.length > 0 ? filters.categories : undefined,
    priorityNetworks:
      filters.priorityNetworks && filters.priorityNetworks.length > 0
        ? filters.priorityNetworks
        : undefined,
    isQpv: filters.isQpv ?? undefined,
    statuses: filters.statuses.length > 0 ? filters.statuses : undefined,
    prospectionStatuses:
      filters.prospectionStatuses.length > 0 ? filters.prospectionStatuses : undefined,
    cities: filters.cities && filters.cities.length > 0 ? filters.cities : undefined,
    departments:
      filters.departments && filters.departments.length > 0 ? filters.departments : undefined,
    search: filters.searchQuery,
    hasDevoirsFaits: filters.hasDevoirsFaits ?? undefined,
    hasInternat: filters.hasInternat ?? undefined,
    renewalNextYear: filters.renewalNextYear ?? undefined,
    conventionSigned: filters.conventionSigned ?? undefined,
    partnershipDateFrom: filters.partnershipDateFrom
      ? toLocalDateString(filters.partnershipDateFrom)
      : undefined,
    partnershipDateTo: filters.partnershipDateTo
      ? toLocalDateString(filters.partnershipDateTo)
      : undefined,
    isProspectionValidated: filters.isProspectionValidated ?? undefined,
    specialtyIds:
      filters.specialtyIds && filters.specialtyIds.length > 0 ? filters.specialtyIds : undefined,
  };
}

/** True when any establishment chip / date / search filter is set. */
export function hasEstablishmentActiveFilters(filters: EstablishmentFilters): boolean {
  return (
    filters.types.length > 0 ||
    (filters.categories && filters.categories.length > 0) ||
    (filters.priorityNetworks && filters.priorityNetworks.length > 0) ||
    (filters.isQpv !== undefined && filters.isQpv !== null) ||
    filters.statuses.length > 0 ||
    filters.prospectionStatuses.length > 0 ||
    (filters.cities && filters.cities.length > 0) ||
    (filters.departments && filters.departments.length > 0) ||
    (filters.calendarTypes && filters.calendarTypes.length > 0) ||
    !!filters.searchQuery?.trim() ||
    filters.createdFrom !== undefined ||
    filters.createdTo !== undefined ||
    (filters.isProspectionValidated !== undefined && filters.isProspectionValidated !== null) ||
    (filters.specialtyIds && filters.specialtyIds.length > 0) ||
    (filters.hasDevoirsFaits !== undefined && filters.hasDevoirsFaits !== null) ||
    (filters.hasInternat !== undefined && filters.hasInternat !== null) ||
    (filters.renewalNextYear !== undefined && filters.renewalNextYear !== null) ||
    (filters.conventionSigned !== undefined && filters.conventionSigned !== null) ||
    filters.partnershipDateFrom !== undefined ||
    filters.partnershipDateTo !== undefined
  );
}
